import React from 'react';
import {
    Avatar,
    Box,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import EditIcon   from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import { RoleBadge, VendorBadge, BoolCell, AVATAR_COLORS, getInitials } from './SharedComponents';

const ROLES   = ['Admin', 'Regular', 'Tourist'];
const VENDORS = ['Ericsson', 'Nokia', 'Both'];

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:hover': { backgroundColor: theme.palette.action.hover },
    '&:last-child td': { border: 0 },
}));

const PrivilegesTable = ({
    privileges,
    modules,
    loading,
    searchText,
    roleFilter,
    vendorFilter,
    onSearchChange,
    onRoleFilterChange,
    onVendorFilterChange,
    onEdit,
    onDelete,
}) => {
    const filtered = privileges.filter((p) => {
        const s = searchText.toLowerCase();
        return (
            (!s || p.userName?.toLowerCase().includes(s) || p.email?.toLowerCase().includes(s)) &&
            (!roleFilter   || p.role   === roleFilter) &&
            (!vendorFilter || p.vendor === vendorFilter)
        );
    });

    return (
        <>
            {/* Filters */}
            <Stack direction="row" spacing={1.5} mb={2} flexWrap="wrap">
                <TextField
                    size="small"
                    placeholder="Search user..."
                    value={searchText}
                    onChange={(e) => onSearchChange(e.target.value)}
                    sx={{ minWidth: 220, flex: 1 }}
                />
                <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Role</InputLabel>
                    <Select value={roleFilter} label="Role" onChange={(e) => onRoleFilterChange(e.target.value)}>
                        <MenuItem value="">All roles</MenuItem>
                        {ROLES.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Vendor</InputLabel>
                    <Select value={vendorFilter} label="Vendor" onChange={(e) => onVendorFilterChange(e.target.value)}>
                        <MenuItem value="">All vendors</MenuItem>
                        {VENDORS.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                    </Select>
                </FormControl>
            </Stack>

            {/* Table */}
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.50' }}>
                            <TableCell sx={{ fontWeight: 500, fontSize: 12 }}>User</TableCell>
                            <TableCell sx={{ fontWeight: 500, fontSize: 12 }}>Role</TableCell>
                            <TableCell sx={{ fontWeight: 500, fontSize: 12 }}>Vendor</TableCell>
                            {modules.map((m) => (
                                <TableCell key={m.id} align="center" sx={{ fontWeight: 500, fontSize: 12 }}>
                                    {m.label}
                                </TableCell>
                            ))}
                            <TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading
                            ? Array.from({ length: 4 }).map((_, i) => (
                                <TableRow key={i}>
                                    {Array.from({ length: 4 + modules.length }).map((_, j) => (
                                        <TableCell key={j}><Skeleton height={24} /></TableCell>
                                    ))}
                                </TableRow>
                            ))
                            : filtered.length === 0
                                ? (
                                    <TableRow>
                                        <TableCell
                                            colSpan={4 + modules.length}
                                            align="center"
                                            sx={{ py: 4, color: 'text.secondary', fontSize: 13 }}
                                        >
                                            No configured users found
                                        </TableCell>
                                    </TableRow>
                                )
                                : filtered.map((p) => {
                                    const ac = AVATAR_COLORS[p.role] || AVATAR_COLORS.Tourist;
                                    return (
                                        <StyledTableRow key={p.userId}>
                                            <TableCell>
                                                <Stack direction="row" spacing={1.5} alignItems="center">
                                                    <Avatar sx={{ width: 30, height: 30, fontSize: 11, bgcolor: ac.bg, color: ac.color }}>
                                                        {getInitials(p.userName)}
                                                    </Avatar>
                                                    <Box>
                                                        <Typography variant="body2" fontWeight={500}>{p.userName}</Typography>
                                                        <Typography variant="caption" color="text.secondary">{p.email}</Typography>
                                                    </Box>
                                                </Stack>
                                            </TableCell>
                                            <TableCell><RoleBadge role={p.role} /></TableCell>
                                            <TableCell><VendorBadge vendor={p.vendor} /></TableCell>
                                            {modules.map((m) => {
                                                const ma = (p.moduleAccess || []).find((a) => a.moduleId === m.id);
                                                return <BoolCell key={m.id} value={ma?.hasAccess} />;
                                            })}
                                            <TableCell align="right">
                                                <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                                    <Tooltip title="Edit">
                                                        <IconButton size="small" onClick={() => onEdit(p)}>
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Remove">
                                                        <IconButton size="small" color="error" onClick={() => onDelete(p)}>
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Stack>
                                            </TableCell>
                                        </StyledTableRow>
                                    );
                                })}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    );
};

export default PrivilegesTable;